import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morph } from '@theme/morph';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    if (!this.isDrawer) {
      this.#disableCartItems();
    }

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          this.#enableCartItems();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        if (this.isDrawer) {
          this.#updateDrawerCartInPlace(parsedResponseText);
        } else {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], 'full');
          this.#updateCartQuantitySelectorButtonStates();
        }

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector =
      this.refs.quantitySelectors?.[line - 1] ??
      this.querySelector(`input[data-cart-line="${line}"]`)?.closest('cart-quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError =
      this.refs[`cartItemError-${line}`] ?? this.querySelector(`[ref="cartItemError-${line}"]`);
    const cartItemErrorContainer =
      this.refs[`cartItemErrorContainer-${line}`] ??
      this.querySelector(`[ref="cartItemErrorContainer-${line}"]`);

    if (cartItemError instanceof HTMLElement) {
      const errorText =
        typeof parsedResponseText.errors === 'string'
          ? parsedResponseText.errors
          : Object.values(parsedResponseText.errors ?? {}).join(' ');
      cartItemError.textContent = errorText;
    }

    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    if (event.detail?.data?.source === 'cart-items-component') return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      if (this.isDrawer) {
        this.#updateDrawerCartInPlace({
          items: event.detail.resource?.items,
          sections: event.detail.data.sections,
        });
      } else {
        morphSection(this.sectionId, cartItemsHtml, 'full');
      }

      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables cart line interactions while an update is in flight.
   */
  #disableCartItems() {
    this.#getCartItemWrappers().forEach((wrapper) => wrapper.classList.add('cart-items-disabled'));
  }

  /**
   * Re-enables cart line interactions on live DOM nodes (morph can detach the instance that started the request).
   */
  #enableCartItems() {
    document.querySelectorAll('.cart-items__wrapper.cart-items-disabled').forEach((wrapper) => {
      wrapper.classList.remove('cart-items-disabled');
    });

    document.querySelectorAll('cart-items-component.cart-items-disabled').forEach((component) => {
      component.classList.remove('cart-items-disabled');
    });

    this.#unlockPageScrollIfStuck();
  }

  /**
   * Updates cart drawer lines and totals without replacing the whole section (keeps +/- buttons working).
   * @param {Object} parsedResponseText
   */
  #updateDrawerCartInPlace(parsedResponseText) {
    const sectionHtml = parsedResponseText.sections?.[this.sectionId];
    if (!sectionHtml) return;

    const parsedDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newRows = parsedDoc.querySelectorAll('.cart-items__table-row');
    const currentRows = this.refs.cartItemRows ?? [];

    newRows.forEach((newRow, index) => {
      const currentRow = currentRows[index];
      if (!currentRow) return;

      const newPrice = newRow.querySelector('.cart-items__final-price');
      const currentPrice = currentRow.querySelector('.cart-items__final-price');
      if (newPrice && currentPrice) {
        currentPrice.innerHTML = newPrice.innerHTML;
      }

      const newQtyInput = newRow.querySelector('input[name="updates[]"]');
      const newQtyDisplay = newRow.querySelector('[ref="quantityDisplay"]');
      let quantity = parsedResponseText.items?.[index]?.quantity;

      if (quantity == null && newQtyInput instanceof HTMLInputElement) {
        quantity = parseInt(newQtyInput.value, 10);
      }

      if (quantity == null && newQtyDisplay?.textContent) {
        quantity = parseInt(newQtyDisplay.textContent, 10);
      }

      const selector = currentRow.querySelector('cart-quantity-selector-component');
      if (!(selector instanceof HTMLElement) || quantity == null || Number.isNaN(quantity)) return;

      const input = selector.querySelector('input[ref="quantityInput"]');
      const display = selector.querySelector('[ref="quantityDisplay"]');

      if (input instanceof HTMLInputElement) {
        input.value = String(quantity);
        input.defaultValue = String(quantity);
        input.setAttribute('data-cart-quantity', String(quantity));
      }

      if (display instanceof HTMLElement) {
        display.textContent = String(quantity);
      }

      /** @type {any} */ (selector).updateButtonStates?.();
    });

    const newSummary = parsedDoc.querySelector('.cart-drawer__summary');
    const currentSummary = this.querySelector('.cart-drawer__summary');
    if (newSummary && currentSummary) {
      morph(currentSummary, newSummary);
    }

    const newCountEl = parsedDoc.querySelector('[ref="cartItemCount"]');
    const currentCountEl = this.querySelector('[ref="cartItemCount"]');
    if (newCountEl && currentCountEl) {
      currentCountEl.textContent = newCountEl.textContent;
    }

    const newHeadingCount = parsedDoc.querySelector('.cart-drawer__count');
    const currentHeadingCount = this.querySelector('.cart-drawer__count');
    if (newHeadingCount && currentHeadingCount) {
      currentHeadingCount.textContent = newHeadingCount.textContent;
    }

    this.updatedCallback();
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Restores scrolling if body was left locked without an open dialog.
   */
  #unlockPageScrollIfStuck() {
    if (document.querySelector('dialog[open]')) return;

    if (document.body.style.position === 'fixed') {
      const scrollY = Math.abs(parseInt(document.body.style.top || '0', 10));
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    }

    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getCartItemWrappers() {
    const wrappers = this.#getCartItemsTargets()
      .map((component) => component.querySelector('.cart-items__wrapper'))
      .filter((wrapper) => wrapper instanceof HTMLElement);

    if (wrappers.length > 0) return wrappers;

    const fallback = this.querySelector('.cart-items__wrapper');
    return fallback instanceof HTMLElement ? [fallback] : [];
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getCartItemsTargets() {
    const selector = this.isDrawer
      ? 'cart-items-component[data-drawer]'
      : 'cart-items-component:not([data-drawer])';
    const targets = Array.from(document.querySelectorAll(selector));
    return targets.length > 0 ? targets : [this];
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
